import React, { useEffect } from 'react';
import { Typography, Box, Alert, Button } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../store';
import StopperComponent from '../components/Stopper/StopperDeveloper';
import ApplicationFormComplate from '../components/Application/ApplicationFormComplate';
import ApplicationStepFive from '../components/Application/ApplicationStepFive';
import ApplicationForm from '../components/Application/ApplicationForm';
import PersonalIdentity from '../components/Identify/PersonalIdentty';
import LegalIdentty from '../components/Identify/LegalIdentty';
import IntroBox from '../components/Intro/IntroBox';
import {
  setCurrentStep,
  setNextStep,
  incrementStep,
} from '../Slice/unifiedFormSlice';
import Contract from 'components/Identify/Contract';
import ApproveStatus from 'components/StatusBox/ApproveStatus';
import useIdentities from 'hooks/useIdentities';
import Breadcrumb from '../components/Navigation/Breadcrumb';

const Wizard: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentStep, nextStep, identity, serverStepState } = useSelector(
    (state: RootState) => state.unifiedForm,
  );
  const { data: identities, isLoading, isError } = useIdentities(true);

  // Extract state from location.
  //
  // "Create a new identity" arrives as `mode: 'new'` with no id, and the
  // handlers for it dispatch resetForm() first — so an empty identity.id is
  // what marks the flow, not the absent location id. Keying off location
  // alone kept the flag true after the identity was created (the router
  // reuses the entry), which nulled the brand new publisherId and produced
  // "شناسه ناشر یافت نشد".
  const isNewIdentity = state?.mode === 'new' && !state?.id && !identity.id;
  const publisherId = state?.id || identity.id || null;
  const displayName =
    state?.display_name || identity.display_name || state?.name || '';
  const identityType = state?.identity_type || identity.type;
  const mode = state?.mode || 'start';

  // Handle mode and step logic
  useEffect(() => {
    if (mode === 'new') {
      if (!publisherId) {
        // If no publisherId, start from step 1 (IntroBox)
        dispatch(setCurrentStep(1));
      } else {
        // If publisherId exists, start from step 2 (ApplicationForm)
        dispatch(setCurrentStep(2));
      }
    }
  }, [mode, publisherId, dispatch]);

  // Handle redirection for multiple identities
  useEffect(() => {
    if (isLoading || isError || !identities) {
      console.log(
        'Skipping identities check: isLoading, isError, or no identities',
      );
      return;
    }

    if (identities.length >= 1 && mode === 'start') {
      console.log('Multiple identities detected, redirecting to dashboard');
      navigate('/dashboard', {
        replace: true,
        state: { error: 'لطفاً یک هویت را انتخاب کنید' },
      });
    }
  }, [identities, isLoading, isError, mode, navigate]);

  // Set step from dashboard or new mode
  useEffect(() => {
    if (state?.nextStep && (mode === 'dashboard' || mode === 'new')) {
      const activeStep = parseInt(state.nextStep);
      dispatch(setCurrentStep(activeStep));
      dispatch(setNextStep(null)); // Reset nextStep after setting
    }
  }, [state, dispatch, mode]);

  // Determine active step. A new identity always begins at ایجاد هویت — a
  // `nextStep` left synced from the previous publisher must not carry over.
  const activeStep = isNewIdentity
    ? currentStep
    : serverStepState === 'synced' && nextStep !== null
      ? nextStep
      : currentStep;

  const handleNext = () => {
    if (serverStepState === 'synced' && nextStep !== null) {
      console.log(
        `Moving from currentStep ${currentStep} to nextStep ${nextStep}`,
      );
      dispatch(setCurrentStep(nextStep));
      dispatch(setNextStep(null)); // Reset nextStep after navigation
    } else {
      console.log(
        `No valid nextStep or server not synced, incrementing currentStep to ${currentStep + 1}`,
      );
      dispatch(incrementStep());
    }
  };

  const MODES = {
    START: 'start',
    NEW: 'new',
    END: 'end',
  };

  if (activeStep >= 2 && !publisherId) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        sx={{ mt: 4 }}
      >
        <Alert severity="error">
          شناسه ناشر یافت نشد. لطفاً از داشبورد یا فلو ثبت وارد شوید.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          بازگشت به داشبورد
        </Button>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
    >
      {/* Only for someone who already has a dashboard to go back to. After
          signup (mode 'start') there are no identities yet, so a trail would
          point at a page the user cannot use. */}
      {mode !== 'start' && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '520px',
            marginTop: '32px',
            // The Stopper carries its own 32px top margin, which would
            // otherwise stack under this and double the gap.
            marginBottom: '-24px',
            textAlign: 'right',
          }}
        >
          <Breadcrumb
            items={[
              { label: 'داشبورد', to: '/dashboard' },
              { label: 'ایجاد هویت و اپلیکیشن' },
            ]}
          />
        </Box>
      )}

      {/* These wrappers are <p> elements, which shrink to their content and
          stopped the cards inside from reaching their own 520px width. */}
      <Typography
        component="div"
        variant="body1"
        sx={{
          paddingBottom: 4,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <StopperComponent activeStep={activeStep} />
      </Typography>

      <Typography
        component="div"
        variant="body1"
        sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      >
        {activeStep === 1 && <IntroBox mode={mode} />}
        {activeStep === 2 && (
          <>
            {console.log('Wizard - State:', {
              publisherId,
              displayName,
              identityType,
              currentStep,
              nextStep,
              mode,
            })}
            <ApplicationForm
              publisherId={String(publisherId || '')}
              displayName={displayName}
            />
          </>
        )}
        {activeStep === 3 && <ApplicationFormComplate />}
        {activeStep === 4 && <ApplicationStepFive />}
        {activeStep === 5 && (
          <>
            {identityType === 'LEGAL' ? (
              <LegalIdentty onNext={handleNext} identityType={identityType} />
            ) : (
              <PersonalIdentity
                onNext={handleNext}
                identityType={identityType}
              />
            )}
          </>
        )}
        {activeStep === 6 && (
          <Typography variant="body1">
            <Contract
              onPrevious={() => dispatch(setCurrentStep(activeStep - 1))}
              idedntty={publisherId}
            />
          </Typography>
        )}
        {activeStep === 7 && (
          <Typography variant="body1">
            <ApproveStatus id={publisherId} display_name={displayName} />
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

export default Wizard;
